import "./ShareLink.styles.scss";
import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Assignment, AssignmentLink } from "types";
import { AssignToggle, Button, Spinner } from "components";
import {
  useGenerateAssignmentLink,
  useFetchAssignmentLink,
  useDeleteAssignmentLink,
} from "utils/hooks";
import {
  useOrganizationSettings,
  getPatientWord,
} from "utils/hooks/organizations/useOrganizationSettings";
import DeleteModal from "../DeleteModal";

interface ShareLinkProps {
  assignment: Assignment;
}

const ShareLink = ({ assignment }: ShareLinkProps) => {
  const { t } = useTranslation("common");
  const { settings: orgSettings } = useOrganizationSettings();
  const [submissionVisible, setSubmissionVisible] = useState(false); //Indicates to show the submission in patient's account after submitting this linked assignment
  const [currentLink, setCurrentLink] = useState<AssignmentLink | null>(null);
  const [visibleLink, setVisibleLink] = useState<AssignmentLink | null>(null);
  const [hiddenLink, setHiddenLink] = useState<AssignmentLink | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { generateAssignmentLink, generatedLink, loading } =
    useGenerateAssignmentLink();
  const {
    fetchAssignmentLink,
    existingLink,
    loading: getLoading,
  } = useFetchAssignmentLink();
  const { deleteAssignmentLink, deleteLoading } = useDeleteAssignmentLink();

  const shareLink = `${process.env.REACT_APP_URL}/p/assignment/${currentLink?.slug}`;

  useEffect(() => {
    if (!assignment) return;
    fetchAssignmentLink({
      assignmentId: assignment.uuid,
      visibleToPatient: submissionVisible,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (existingLink && existingLink.visibleToPatient) {
      setVisibleLink(existingLink);
    } else if (existingLink && !existingLink.visibleToPatient) {
      setHiddenLink(existingLink);
    }
  }, [existingLink]);

  useEffect(() => {
    if (generatedLink && generatedLink.visibleToPatient) {
      setVisibleLink(generatedLink);
    } else if (generatedLink && !generatedLink.visibleToPatient) {
      setHiddenLink(generatedLink);
    }
  }, [generatedLink]);

  useEffect(() => {
    setCurrentLink(submissionVisible ? visibleLink : hiddenLink);
  }, [visibleLink, hiddenLink, submissionVisible, setCurrentLink]);

  const handleLinkCopy = async () => {
    try {
      await navigator.clipboard
        .writeText(shareLink)
        .then(() => toast("Link copied to clipboard!"));
    } catch {
      toast.error("Oops, failed to copy");
    }
  };

  const handleGenerateLink = async () => {
    await generateAssignmentLink({
      assignmentId: assignment.uuid,
      visibleToPatient: submissionVisible,
    });
  };

  const handleChangeToggle = (e: any) => {
    setSubmissionVisible(e.target.checked);
    setCurrentLink(null);
    if (e.target.checked && visibleLink) {
      setCurrentLink(visibleLink);
    } else if (
      (e.target.checked && !visibleLink) ||
      (!e.target.checked && !hiddenLink)
    ) {
      fetchAssignmentLink({
        assignmentId: assignment.uuid,
        visibleToPatient: e.target.checked,
      });
    } else if (!e.target.checked && hiddenLink) {
      setCurrentLink(hiddenLink);
    }
  };

  const handleDeleteLink = async () => {
    if (!currentLink) return;
    const result = await deleteAssignmentLink({ linkId: currentLink.uuid });
    if (result) {
      submissionVisible ? setVisibleLink(null) : setHiddenLink(null);
      setCurrentLink(null);
    }
    setIsDeleteOpen(false);
  };

  return (
    <div className="share-settings">
      <p className="share-settings_hint">
        <Trans
          i18nKey={"common:share_link.share_link_hint"}
          values={{ label: assignment?.label }}
          components={{ span: <b /> }}
        />
      </p>
      <AssignToggle
        checked={submissionVisible}
        label={t("workbook.fill_in_submission_visible", {
          patientWord: getPatientWord(orgSettings),
        })}
        labelStyle="share-settings_toggle"
        onChange={handleChangeToggle}
      />
      <div className="share-settings_toggle-hint">
        <Trans
          i18nKey={"common:workbook.fill_in_submiss_visible_hint"}
          components={{ br: <br /> }}
          values={{
            patientWord: getPatientWord(orgSettings),
          }}
        />
      </div>
      {currentLink ? (
        <>
          <div className="share-settings_buttons">
            <Link to={shareLink} target="_blank">
              <Button
                value={t("invite_code.link_label")}
                name="share-assignm-link-btn"
                icon={"Link2"}
                extraClass="assignment-card_btn"
              />
            </Link>
            <Button
              value={
                t("invite_code.copy_label") + " " + t("invite_code.link_label")
              }
              name="copy-assignm-link-btn"
              icon={"Copy2"}
              extraClass="assignment-card_btn"
              onClick={handleLinkCopy}
            />
            <Button
              value={t("common_labels.delete")}
              name="delete-assignm-link-btn"
              icon={"Remove"}
              extraClass="assignment-card_btn"
              onClick={() => setIsDeleteOpen(true)}
            />
          </div>
          <div className="share-settings_link">
            <Link to={shareLink} target="_blank">
              {shareLink}
            </Link>
          </div>
        </>
      ) : getLoading ? (
        <div className="share-settings_spinner">
          <Spinner isActive={true} />
        </div>
      ) : (
        <Button
          name="generate-link-btn"
          value={"Generate link"}
          pill
          extraClass="thin"
          isLoading={loading}
          onClick={handleGenerateLink}
        />
      )}
      <DeleteModal
        isOpen={isDeleteOpen}
        label={t("share_link.public_link")}
        hint={t("confirmations.confirm_delete_body")}
        fullScreen={true}
        loading={deleteLoading}
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteLink}
      />
    </div>
  );
};

export default ShareLink;
